import React, { useEffect, useState } from "react";
import useEscapeLayer from "../useEscape";
import {
  splitControls,
  isYellow,
  pendingGroups,
  bulkFailure,
  wakeChoices,
  wakeDate,
  wakeSentence,
  receiptStoreKey,
  keepReceipts,
  sameReceipts,
  shownReceipts,
} from "./nextListView.mjs";
import { ymd, addCalendar } from "./statusConfirm.mjs";
import DateField from "../datepicker/DateField";
import { byCall, tickPath } from "../callActions.mjs";
import { shortDate } from "./format.mjs";

/*
 * NEXT: every owed action on its own named line, and the only place its button
 * lives (frames 1a, 1c, 1d, 1g; the after-send receipt in 1i).
 *
 * The lines, their words and which button is THE yellow come from record.next
 * (record/nextModel.mjs), computed once by the shell; how a line is drawn comes
 * from record/nextListView.mjs. The rules, each a bug on the old record:
 *   - only the top line's first control is yellow; every other control is white
 *     with the same verb (the old card had three yellows and a Nudge under a
 *     client's "done");
 *   - every line ends in a small × ("Not needed, or done by hand"), which hides it
 *     until something newer happens, or for good on the welcome and the finance
 *     intro (db/crm.js dismissAction owns that rule, the tooltip says it);
 *   - an overdue line gets a muted grey fill, never red: red is for warnings, and
 *     an action being late is the normal state of a queue, not an alarm;
 *   - a reply waiting carries the pulsing red dot, the page's one moving thing.
 *
 * The head says "3 to do" in the black count badge, not "3 owed": owed is the name
 * of one kind of line (a deliverable I promised), and a count that includes a
 * waiting approval and a wake-up is not that (reality.txt [25]).
 *
 * WHAT IT HANDLES IN PLACE, so nothing navigates away from the record:
 *   Review            the waiting changes open under their line, grouped into
 *                     verdicts, with Approve and Dismiss through the existing
 *                     /pending-changes/bulk route (reality.txt [6])
 *   Move the wake-up  the Remove confirm's own reminder chips, then Save
 * Everything else goes through ops.control, which opens the one composer, the
 * Remove from pipeline confirm, Mark sent, Stop reminders or Held / No-show. A
 * control that fails shows its error beside the line it belongs to.
 *
 * THE RECEIPTS. After a send the line becomes "✓ Replied to Ella Moss · just now"
 * (owed is the exception: an email never clears it, nextModel rewords that line
 * instead). The shell keeps this visit's receipts across the reload that follows
 * the send; the card also keeps them in sessionStorage for an hour, so closing the
 * panel and opening it again from Today still says it went.
 *
 * On the phone the yellow lives in the bottom bar (PhoneBar), so the top line here
 * shows no button of its own and says "in the bar below".
 *
 * Props:
 *   record   the shell's bundle: next, composer (its line reads pressed while
 *            open), layout, company, contact, now
 *   ops      ops.control(line, control), ops.dismiss(line), ops.patchCompany,
 *            ops.call(path, options), ops.reload
 */

// sessionStorage can be missing or refuse (a private window, blocked site data):
// the receipts are a convenience, so every read and write may quietly fail.
function readStore(key) {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function writeStore(key, list) {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Nothing to do: the shell still has this visit's receipts.
  }
}

const errorText = (e) => (e && (e.message || String(e))) || "That didn’t work. Try again.";

export default function NextList({ record, ops }) {
  const next = record.next;
  const phone = record.layout === "phone";
  const now = record.now || new Date();
  const company = record.company;

  // One thing open in place at a time: a line's waiting changes, or its wake-up.
  const [open, setOpen] = useState(null); // { line, what: "review" | "move" }
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState({});
  const [wake, setWake] = useState({ choice: "3m", date: "" });

  const storeKey = receiptStoreKey({ company, contact: record.contact });
  const [stored, setStored] = useState(() => readStore(storeKey));
  useEffect(() => setStored(readStore(storeKey)), [storeKey]);
  const liveReceipts = next ? next.receipts : null;
  useEffect(() => {
    if (!liveReceipts || !liveReceipts.length) return;
    const merged = keepReceipts(stored, liveReceipts, new Date());
    if (sameReceipts(stored, merged)) return;
    writeStore(storeKey, merged);
    setStored(merged);
  }, [liveReceipts, stored, storeKey]);

  // What is open, only while its line is still there: a verdict or a wake-up move
  // that cleared the line must not leave an Esc layer behind that eats a press.
  const openLine = open && next && next.lines.some((l) => l.key === open.line) ? open : null;
  // Esc closes what is open in place before anything behind it (useEscape.js).
  useEscapeLayer(!!openLine, () => setOpen(null));

  if (!next) return null;
  const receipts = shownReceipts(stored, next, now);
  if (!next.lines.length && !receipts.length && !next.states.length) return null;

  const setError = (key, message) => setErrors((x) => ({ ...x, [key]: message || null }));
  const setBusyKey = (key, on) => setBusy((x) => ({ ...x, [key]: on }));
  const isOpen = (line, what) => !!openLine && openLine.line === line.key && openLine.what === what;
  const toggle = (line, what) => {
    setError(`${line.key}:${what}`, null);
    if (what === "move") setWake({ choice: "3m", date: "" });
    setOpen((o) => (o && o.line === line.key && o.what === what ? null : { line: line.key, what }));
  };

  const run = async (line, control) => {
    if (control.id === "review" || control.id === "move_wakeup") {
      toggle(line, control.id === "review" ? "review" : "move");
      return;
    }
    const key = `${line.key}:${control.id}`;
    setError(key, null);
    setBusyKey(key, true);
    try {
      // ops.control answers false for a control it has nothing to do with here
      // (Held / No-show with no meeting on the timeline): say so rather than
      // leave a button that silently does nothing.
      const done = await ops.control(line, control);
      if (done === false) setError(key, "That isn’t available on this record yet.");
    } catch (e) {
      setError(key, errorText(e));
    } finally {
      setBusyKey(key, false);
    }
  };

  const dismiss = async (line) => {
    const key = `${line.key}:dismiss`;
    setError(key, null);
    setBusyKey(key, true);
    try {
      // Resolves after the shell's reload, so the line is already gone by then.
      await ops.dismiss(line);
    } catch (e) {
      setError(key, errorText(e));
    } finally {
      setBusyKey(key, false);
    }
  };

  // One verdict on a group of waiting changes. The bulk route answers per row, so a
  // refusal ("this company is out of the pipeline now") reads beside its buttons
  // instead of the single route's bare 500.
  const verdict = async (group, action) => {
    const key = `review:${group.key}`;
    setError(key, null);
    setBusyKey(key, action);
    try {
      const res = await ops.call("/pending-changes/bulk", { method: "POST", body: JSON.stringify({ ids: group.ids, action }) });
      const failed = bulkFailure(res && res.results);
      if (failed) setError(key, failed);
      await ops.reload();
    } catch (e) {
      setError(key, errorText(e));
    } finally {
      setBusyKey(key, false);
    }
  };

  const moveWake = async (line) => {
    const key = `${line.key}:move`;
    const day = wakeDate(wake.choice, { now: new Date(), date: wake.date });
    if (!day) return;
    setError(key, null);
    setBusyKey(key, true);
    try {
      await ops.patchCompany({ recycle_until: day });
      setOpen(null);
    } catch (e) {
      setError(key, errorText(e));
    } finally {
      setBusyKey(key, false);
    }
  };

  const button = (line, i, c) => {
    const key = `${line.key}:${c.id}`;
    // A Proposed control is shown for its words only: dashed, grey and dead.
    if (c.proposed) {
      return (
        <span key={c.id} className="rec-proposed-wrap">
          <span className="rec-inert rec-nl__inert">{c.label}</span>
          <span className="rec-proposed">Proposed</span>
        </span>
      );
    }
    const yellow = isYellow(next, i, c, { phone });
    const composing = c.id === "compose" && record.composer && record.composer.key === line.key;
    const opened = (c.id === "review" && isOpen(line, "review")) || (c.id === "move_wakeup" && isOpen(line, "move"));
    return (
      <button
        key={c.id}
        type="button"
        className={`rec-btn ${yellow ? "rec-btn--primary" : "rec-btn--secondary"}${composing || opened ? " is-pressed" : ""}`}
        aria-pressed={c.id === "compose" ? !!composing : undefined}
        aria-expanded={c.id === "review" || c.id === "move_wakeup" ? opened : undefined}
        disabled={!!busy[key]}
        onClick={() => run(line, c)}
      >
        {c.label}
      </button>
    );
  };

  const reviewPanel = (line) => {
    const groups = pendingGroups(line.items);
    return (
      <div className="rec-nl__panel" role="region" aria-label="Changes waiting in Approvals">
        {groups.map((g) => {
          const key = `review:${g.key}`;
          return (
            <div className="rec-nl__change" key={g.key}>
              <div className="rec-nl__changetext">
                {g.lines.map((text, j) => (
                  <div key={j}>{text}</div>
                ))}
              </div>
              {g.evidence && <div className="rec-nl__evidence">{g.evidence}</div>}
              <div className="rec-nl__acts">
                {errors[key] && <span className="rec-err">{errors[key]}</span>}
                {g.source_url && (
                  <a className="rec-nl__link" href={g.source_url} target="_blank" rel="noopener noreferrer">
                    Source ↗
                  </a>
                )}
                <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={!!busy[key]} onClick={() => verdict(g, "dismiss")}>
                  {busy[key] === "dismiss" ? "Dismissing…" : "Dismiss"}
                </button>
                <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={!!busy[key]} onClick={() => verdict(g, "approve")}>
                  {busy[key] === "approve" ? "Approving…" : "Approve"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // A call's action items, each ticked off here (POST /activities/:id/promises);
  // the reload drops a ticked one, and the line with the last.
  const tickPromise = async (line, item) => {
    const key = `${line.key}:tick`;
    setError(key, null);
    setBusyKey(`${line.key}:${item.key}`, true);
    try {
      await ops.call(tickPath(item), { method: "POST", body: JSON.stringify({ key: item.key, done: true }) });
      await ops.reload();
    } catch (e) {
      setError(key, errorText(e));
      setBusyKey(`${line.key}:${item.key}`, false);
    }
  };

  const promisesPanel = (line) => (
    <div className="rec-nl__promises">
      {byCall(line.promises).map((g) => (
        <div key={g.activity_id} className="rec-nl__promisecall">
          <span className="rec-label">From the {shortDate(g.call_at, { now })} call</span>
          {g.items.map((item) => (
            <label key={item.key} className="rec-nl__promise" title={item.quote ? `“${item.quote}”` : undefined}>
              <input
                type="checkbox"
                checked={!!busy[`${line.key}:${item.key}`]}
                disabled={!!busy[`${line.key}:${item.key}`]}
                onChange={() => tickPromise(line, item)}
              />
              <span>{item.text}</span>
            </label>
          ))}
        </div>
      ))}
      {errors[`${line.key}:tick`] && <span className="rec-err">{errors[`${line.key}:tick`]}</span>}
    </div>
  );

  const movePanel = (line) => {
    const key = `${line.key}:move`;
    const day = wakeDate(wake.choice, { now, date: wake.date });
    return (
      <div className="rec-nl__panel" role="region" aria-label="Move the wake-up">
        <span className="rec-label">Remind me again</span>
        <div className="rec-chips">
          {wakeChoices().map((w) => (
            <button
              key={w.id}
              type="button"
              className={`rec-chip${wake.choice === w.id ? " is-on" : ""}`}
              aria-pressed={wake.choice === w.id}
              onClick={() => setWake((x) => ({ ...x, choice: w.id }))}
            >
              {w.label}
            </button>
          ))}
        </div>
        {wake.choice === "date" && (
          <DateField
            className="rec-nl__date"
            aria-label="New wake-up date"
            min={ymd(addCalendar(now, { days: 1 }))}
            value={wake.date}
            onChange={(d) => setWake((x) => ({ ...x, date: d }))}
          />
        )}
        {day && <div className="rec-body">{wakeSentence(day, company && company.name)}</div>}
        <div className="rec-nl__acts">
          {errors[key] && <span className="rec-err">{errors[key]}</span>}
          <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={!!busy[key]} onClick={() => setOpen(null)}>
            Cancel
          </button>
          <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={!day || !!busy[key]} onClick={() => moveWake(line)}>
            {busy[key] ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="rec-card rec-card--next rec-nl" aria-label="Next">
      <div className="rec-next__head rec-nl__head">
        <span className="rec-label">Next</span>
        {next.count > 0 && <span className="rec-count">{next.countLabel}</span>}
      </div>

      {receipts.map((r) => (
        <div className="rec-nl__item rec-nl__receipt" key={r.key}>
          <span className="rec-check" aria-hidden="true">✓</span>
          <span className="rec-nl__receipttext">{r.text}</span>
          {r.thread_url && (
            <a className="rec-nl__link" href={r.thread_url} target="_blank" rel="noopener noreferrer">
              Open the thread ↗
            </a>
          )}
        </div>
      ))}

      {next.lines.map((line, i) => {
        const yellowHere = !!next.yellow && next.yellow.line === i;
        const { inline, below } = splitControls(line, { phone, yellowHere });
        // An error sits by the control that failed: under the buttons beside the
        // title (right-aligned, where they are), or at the end of the row below.
        const errorsOf = (controls) => controls.map((c) => errors[`${line.key}:${c.id}`]).filter(Boolean);
        const lineErrors = [...errorsOf(inline), errors[`${line.key}:dismiss`]].filter(Boolean);
        const belowErrors = errorsOf(below);
        const detail = [line.detail, phone && yellowHere && line.controls.length ? "in the bar below" : null].filter(Boolean).join(" · ");
        return (
          <div
            key={line.key}
            className={`rec-nl__item${line.overdue ? " is-overdue" : ""}${busy[`${line.key}:dismiss`] ? " is-busy" : ""}`}
          >
            {/* The buttons and the × travel together: on a narrow panel or the
                phone they drop under the words as one right-aligned group, so the ×
                still ends the line it belongs to. */}
            <div className="rec-nl__line">
              {line.pulse && <span className="rec-pulse" title="A reply is waiting on you" />}
              <div className="rec-nl__body">
                <div className="rec-nl__title">{line.title}</div>
                {detail && <div className="rec-nl__detail">{detail}</div>}
              </div>
              <div className="rec-nl__side">
                {inline.length > 0 && <div className="rec-nl__controls">{inline.map((c) => button(line, i, c))}</div>}
                {line.dismiss && (
                  <button
                    type="button"
                    className="rec-x"
                    title={line.dismiss.title}
                    aria-label={`${String(line.dismiss.title).replace(/\.$/, "")}: ${line.title}`}
                    disabled={!!busy[`${line.key}:dismiss`]}
                    onClick={() => dismiss(line)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            {below.length > 0 && (
              <div className="rec-nl__more">
                {below.map((c) => button(line, i, c))}
                {belowErrors.length > 0 && <span className="rec-err">{belowErrors.join(" ")}</span>}
              </div>
            )}
            {lineErrors.length > 0 && <div className="rec-err rec-nl__err">{lineErrors.join(" ")}</div>}
            {line.promises && line.promises.length > 0 && promisesPanel(line)}
            {line.kind === "pending" && isOpen(line, "review") && reviewPanel(line)}
            {isOpen(line, "move") && movePanel(line)}
          </div>
        );
      })}

      {next.states.map((s) => (
        <div className="rec-nl__state" key={s}>{s}</div>
      ))}
      {next.notes.map((n) => (
        <div className="rec-next__note rec-nl__note" key={n}>{n}</div>
      ))}
    </section>
  );
}
